import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { QueryField, QueryInfo } from "system/query/QueryInfo";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { ColumnDialogMode, ColumnModel, ColumnRenderType } from "components/tables/columns/ColumnModel";
import { DraftColumn } from "./DataTableSettings";
import DataTableColumnSelector, { CustomColumnFieldType } from "components/tables/columns/DataTableColumnSelector";

interface DataTableDialogContentProps {
  activeColumnIndex: number | undefined;
  // Actions for Column Dialog: AddColumn | UpdateCategory | UpdateColumn
  columnDialogMode: ColumnDialogMode | undefined;
  columnModelState: ColumnModel;
  draftColumn: DraftColumn | undefined;
  handleSetDraftColumn: (draftColumn: DraftColumn) => void;
  handleSetLocalCategory: (category: QueryField) => void;
  // This category is the basis for sub aggregations
  localCategory: QueryField | undefined;
  queryInfo: QueryInfo;
  updateColumnModel: (newColumnModel: ColumnModel) => void;
}

export default function DataTableDialogContent(props: DataTableDialogContentProps) {
  const { i18n } = useTranslation();

  const additionalFields: CustomColumnFieldType[] = [
    {
      id: "percent",
      label: i18n.t("percent-of-total"),
      type: "integer",
      renderType: ColumnRenderType.Percent,
      // The "_social_field_category" field keeps the
      // additional items grouped nicely in the UI options list.
      "_social_field_category": "other",
      showRenderTypeInSettings: true
    },
    {
      id: "ratio",
      label: i18n.t("tables.ratio"),
      type: "integer",
      renderType: ColumnRenderType.Text,
      "_social_field_category": "other",
      showRenderTypeInSettings: false
    },
    {
      id: "percent_change",
      label: i18n.t("tables.percent-change"),
      type: "integer",
      renderType: ColumnRenderType.PercentChange,
      "_social_field_category": "other",
      showRenderTypeInSettings: false
    },
  ];

  const additionalFieldOps: Record<string, { id: string, label: string }[]> = {
    "percent": [
      {
        id: "percent",
        label: i18n.t("percent"),
      },
      {
        id: "distributionBar",
        label: i18n.t("tables.column-render-types.distribution-bar"),
      },
    ]
  };

  return (
    <>
      {props.columnDialogMode === ColumnDialogMode.UPDATE_CATEGORY &&
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            width: "100%"
          }}>
          <Typography sx={{ flex: 1 }}>{i18n.t("field")}</Typography>
          <Box sx={{ flex: 2 }}>
            <QueryFieldSelector
              disableClearable
              requireAggSupported={true}
              fields={props.queryInfo.fields || []}
              allowedFieldTypes={["keyword", "keywords"]}
              selectedFieldId={props.localCategory?.id || "sentiment"}
              setSelectedFieldId={(newFieldId: string) => {
                const newSelectedField = props.queryInfo.fields?.find((field: QueryField) => {
                  return field.id === newFieldId;
                });
                newSelectedField && props.handleSetLocalCategory(newSelectedField);
              }}
            />
          </Box>
        </Stack>
      }
      {(props.columnDialogMode === ColumnDialogMode.ADD_COLUMN ||
        props.columnDialogMode === ColumnDialogMode.UPDATE_COLUMN) && (
        <DataTableColumnSelector
          activeColumnIndex={props.activeColumnIndex}
          additionalFields={additionalFields}
          additionalFieldOps={additionalFieldOps}
          columnDialogMode={props.columnDialogMode}
          draftColumn={props.draftColumn}
          localCategory={props.localCategory}
          onSetDraftColumn={props.handleSetDraftColumn}
          queryInfo={props.queryInfo}
        />
      )}
    </>
  );
}
